import React, { useState } from 'react';
import { withFormik, Field, Form, getIn } from 'formik';
import { Button, Row, Col, Spinner } from 'react-bootstrap';
import * as Yup from 'yup';
import { useDispatch } from 'react-redux';
import { InputSelect, EmployeeSearchDataTable } from '../../components';
import { camelCaseEmptyStringRecursive } from '../../services/utils';
import { indexDocumentTemplateRequest } from '../../requests/documentTemplates';

const selectorTypes = [{ label: 'Todos', value: 'all_employees' }];

const RiskPreventionInternalDocumentationForm = ({
  errors,
  touched,
  values,
  setFieldValue,
  isSubmitting,
  templatePdf,
  onRequest
}) => {
  const [selector, setSelector] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [selectedCount, setSelectedCount] = useState(0);
  const dispatch = useDispatch();
  const documentType = [
    'personal_protection',
    'report_occupational_hazards',
    'acceptance_riohs',
    'induction_risk_prevention'
  ];

  const fetchDocumentTemplates = (inputValue, callback) => {
    indexDocumentTemplateRequest({
      dispatch,
      params: {
        query: inputValue,
        template_type: documentType,
        sort_column: 'name',
        display_length: 150
      },
      successCallback: response => callback(response.data.data)
    });
  };

  const handleSuccessEmployees = response => {
    const vEmployees = camelCaseEmptyStringRecursive(response?.data?.data) || [];
    const { employees } = templatePdf;
    const employeeIds = employees.map(item => item.id);

    const employeesToAdd = vEmployees.filter(selected => !employeeIds.includes(selected.id));
    const vSelector = [...selectorTypes, ...employeesToAdd];
    setSelector(vSelector);
  };

  const removeFromTable = (employee, action) => {
    if (action === 'destroy') {
      const { employees: templatePdfEmployees } = values.templatePdf;
      const notRemovedEmployees = templatePdfEmployees.filter(selected => selected.id !== employee.id);
      const addToSelector = [...selector, employee];
      setSelector(addToSelector);
      setFieldValue('templatePdf[employees]', notRemovedEmployees);
      if (notRemovedEmployees.length === 0) setFieldValue('templatePdf[employeeIds]', ['']);
      else
        setFieldValue(
          'templatePdf[employeeIds]',
          notRemovedEmployees.map(item => item.id)
        );
    }
  };

  const removeAllFromTable = () => {
    const selectedRowsIds = selectedRows.map(item => item.id);
    const notRemovedEmployees = values.templatePdf.employees.filter(selected => !selectedRowsIds.includes(selected.id));
    setSelector([...selector, ...selectedRows, ...selectorTypes].filter(item => item.label));
    setFieldValue('templatePdf[employees]', notRemovedEmployees);
    setFieldValue(
      'templatePdf[employeeIds]',
      notRemovedEmployees.map(item => item.id)
    );
    setSelectedCount(0);
    setSelectedRows([]);
  };

  const handleSelectedRows = item => {
    setSelectedRows(item.selectedRows);
    setSelectedCount(item.selectedCount);
  };

  const addAllEmployees = () => {
    const { templatePdf: templatePdfValues } = values;
    const vSelector = selector.filter(selected => selected.id !== undefined);
    const selectedEmployeeIds = vSelector.map(employee => employee.value);
    const selectedEmployees = vSelector.map(employee => ({ ...employee }));
    setSelector([selectorTypes]);
    setFieldValue('employeeSelector', '');
    setFieldValue('templatePdf[employees]', [...selectedEmployees, ...templatePdfValues.employees]);
    setFieldValue('templatePdf[employeeIds]', [...selectedEmployeeIds, ...templatePdfValues.employeeIds]);
  };

  const handleSelector = () => {
    const vSelector = selector.filter(selected => selected.value !== values.employeeSelector);
    if (vSelector.length === selector.length) return;
    if (values.employeeSelector === 'all_employees') {
      addAllEmployees();
    } else {
      const employeeToAdd = selector.find(selected => selected.value === values.employeeSelector);
      setFieldValue(`templatePdf[employeeIds]`, [...values.templatePdf.employeeIds, employeeToAdd.value]);
      setFieldValue('templatePdf[employees]', [...values.templatePdf.employees, employeeToAdd]);
      setSelector(vSelector);
    }
  };

  const handleSearch = selectedEmployees => {
    const vSelector = selector.filter(selected => !selectedEmployees.includes(selected.value));
    if (vSelector.length === selector.length) return;
    const employeesToAdd = selector.filter(selected => selectedEmployees.includes(selected.value));
    setFieldValue(`templatePdf[employeeIds]`, [
      ...values.templatePdf.employeeIds,
      ...employeesToAdd.map(item => item.value)
    ]);
    setFieldValue('templatePdf[employees]', [...values.templatePdf.employees, ...employeesToAdd]);
    setSelector(vSelector);
  };

  return (
    <>
      {onRequest && (
        <div className="containerSpinnerLoad fix-middle center-spinner position-fixed">
          <Spinner animation="border" variant="primary" />
        </div>
      )}
      <Form className={`${onRequest && 'bg-opacity'}`}>
        <Row>
          <Col md={6}>
            <Field name="templatePdf[templateId]">
              {({ field }) => (
                <InputSelect
                  {...field}
                  abbr
                  label="Documentos"
                  placeholder="Seleccionar documento"
                  value={undefined}
                  request={fetchDocumentTemplates}
                  onChange={data => {
                    setFieldValue(field.name, data ? data.value : '');
                    setFieldValue(
                      'templatePdf[documentType]',
                      documentType.find(item => item === data.template_type)
                    );
                  }}
                  error={getIn(errors, field.name)}
                  touched={getIn(touched, field.name)}
                />
              )}
            </Field>
          </Col>
        </Row>
        <div className="info-box ml-0 mt-4">
          <h4 className="text-uppercase">Selecciona Trabajadores</h4>
          <p className="info">
            <span className="full-width">Seleccione trabajadores a los que se le asignará este documento</span>
          </p>
        </div>
        <EmployeeSearchDataTable
          baseModel={templatePdf}
          employees={values.templatePdf.employees}
          handleActions={removeFromTable}
          handleMassAction={removeAllFromTable}
          handleSearch={handleSearch}
          handleSelectedRows={handleSelectedRows}
          handleSelector={handleSelector}
          handleSuccessEmployees={handleSuccessEmployees}
          options={selector}
          selectedCount={selectedCount}
        />
        <Row className="d-flex justify-content-end my-5">
          <Col md={3}>
            <Button type="submit" disabled={isSubmitting || values.templatePdf.employees.length === 0} block>
              Guardar
            </Button>
          </Col>
        </Row>
      </Form>
    </>
  );
};

const setInitialValues = props => {
  const { employees, employeeIds } = props.templatePdf;

  return {
    templatePdf: {
      employees,
      employeeIds,
      templateId: ''
    }
  };
};

const validationSchema = Yup.object().shape({
  templatePdf: Yup.object().shape({
    templateId: Yup.string().required('Debes seleccionar un documento')
  })
});

const handleSubmit = (values, { props, setSubmitting }) => {
  const { formRequest } = props;
  formRequest(values, setSubmitting);
};

export default withFormik({
  mapPropsToValues: setInitialValues,
  validationSchema,
  handleSubmit,
  enableReinitialize: true,
  validateOnMount: props => props.action !== 'new'
})(RiskPreventionInternalDocumentationForm);
